/**
 * 真人出镜样片墙「先看看别人怎么拍」(0924 车 D 二期,契约 §3.4)。
 *
 * 数据:`GET /video/templates?type=REAL_PERSON`(复用 `xb_video_template`,首批 8 条种子)。
 * 卡 = 预览样片 + 标题 + 一句脚本摘要 + 推荐时长/画幅 + 预估积分 +「用这个拍」。
 *
 * ★样片由主会话后补(老板 0924 拍板②:样片用虚拟人),种子 `preview_video_key` 先 NULL ——
 *   没有样片时卡面显示「样片制作中」占位,是**正常态**不是错误态,整张卡照常可用。
 * ★空/错:整块不渲染(不挂一个空架子占首屏);加载中给骨架。
 * ★防御:D-api 已实现 `?type=` 过滤(且不带 type 的列表排除 REAL_PERSON);客户端仍按 type
 *   再筛一遍 —— 老 api(忽略 type 参数回全量)下 PROMO/DRAMA 也不会混进真人样片墙。
 */
import { useQuery } from "@tanstack/react-query";
import { Clapperboard, Film } from "lucide-react";

import { Button } from "@/shared/ui/button";
import { cn } from "@/shared/lib/utils";
import { CARD_WALL_RADIUS } from "@/shared/ui/cardWall";
import { usePriceBook } from "@/entities/pricing/model/usePriceBook";
import {
  REAL_PERSON_TEMPLATE_TYPE,
  videoService,
  type VideoTemplateDto,
} from "../../../api/videoService";
import { estimateTemplateCredits, templateSummary } from "../../../model/realPersonIntake";
import { useTenantIndustryName } from "../../../model/useTenantIndustryCode";

const REAL_PERSON_TEMPLATES_QUERY_KEY = ["video", "templates", REAL_PERSON_TEMPLATE_TYPE];

export function RealPersonSampleWall({
  activeTemplateId,
  onUse,
}: {
  activeTemplateId: number | null;
  onUse: (t: VideoTemplateDto) => void;
}) {
  const q = useQuery({
    queryKey: REAL_PERSON_TEMPLATES_QUERY_KEY,
    queryFn: () => videoService.listTemplates({ type: REAL_PERSON_TEMPLATE_TYPE }),
    meta: { suppressGlobalErrorToast: true },
  });
  const { book } = usePriceBook();
  // 1004 T6:摘要里的「{行业}」换成本租户行业(没有就删掉),不再露「行业公司老板」
  const industryName = useTenantIndustryName();
  const templates = (q.data ?? []).filter(
    (t) => t.type === REAL_PERSON_TEMPLATE_TYPE && t.enabled !== false
  );

  if (q.isLoading) {
    return (
      <section data-testid="real-sample-wall-loading" className="flex gap-3 overflow-x-auto pb-1">
        {Array.from({ length: 4 }).map((_, i) => (
          <div
            key={i}
            className={cn("fx-skeleton h-[248px] w-[208px] shrink-0", CARD_WALL_RADIUS)}
          />
        ))}
      </section>
    );
  }
  if (q.isError || templates.length === 0) return null;

  return (
    <section data-testid="real-sample-wall" aria-label="先看看别人怎么拍">
      <div className="mb-2.5 flex items-baseline gap-2">
        <Clapperboard className="h-4 w-4 self-center text-primary" />
        <span className="text-sm font-semibold">先看看别人怎么拍</span>
        <span className="text-2xs text-muted-foreground">
          挑一条「用这个拍」,脚本和参数替你填好,改成你的内容就能出片
        </span>
      </div>
      <div className="flex gap-3 overflow-x-auto pb-1">
        {templates.map((t) => (
          <SampleCard
            key={t.id}
            t={t}
            active={activeTemplateId === t.id}
            credits={estimateTemplateCredits(book, t)}
            industryName={industryName}
            onUse={() => onUse(t)}
          />
        ))}
      </div>
    </section>
  );
}

function SampleCard({
  t,
  active,
  credits,
  industryName,
  onUse,
}: {
  t: VideoTemplateDto;
  active: boolean;
  credits: number | null;
  industryName?: string;
  onUse: () => void;
}) {
  return (
    <div
      data-testid={`real-sample-card-${t.id}`}
      data-active={active || undefined}
      className={cn(
        "flex w-[208px] shrink-0 flex-col overflow-hidden border bg-card",
        CARD_WALL_RADIUS,
        active ? "border-primary shadow-[0_0_0_1px_hsl(var(--primary))]" : "border-border/70"
      )}
    >
      <div className="relative h-[124px] bg-muted">
        {t.previewVideoUrl ? (
          // 样片静音循环,悬停播放(不自动播 8 条视频抢带宽)。
          <video
            data-testid={`real-sample-video-${t.id}`}
            src={t.previewVideoUrl}
            poster={t.coverUrl}
            muted
            loop
            playsInline
            preload="metadata"
            className="h-full w-full object-cover"
            onMouseEnter={(e) => void e.currentTarget.play().catch(() => {})}
            onMouseLeave={(e) => e.currentTarget.pause()}
          />
        ) : (
          <div
            data-testid={`real-sample-placeholder-${t.id}`}
            className="flex h-full flex-col items-center justify-center gap-1.5 text-muted-foreground"
          >
            <Film className="h-6 w-6 opacity-60" />
            <span className="text-2xs">样片制作中</span>
          </div>
        )}
      </div>
      <div className="flex flex-1 flex-col gap-1.5 p-3">
        <div className="truncate text-sm font-semibold" title={t.name}>
          {t.name}
        </div>
        <p className="line-clamp-2 min-h-[2.5em] text-2xs leading-relaxed text-muted-foreground">
          {templateSummary(t.briefScript, 40, industryName)}
        </p>
        <div className="flex flex-wrap items-center gap-x-2 text-2xs text-muted-foreground">
          <span>
            推荐 {t.preferredDurationSec}s · {t.preferredRatio}
          </span>
          {credits != null && (
            <span data-testid={`real-sample-credits-${t.id}`}>
              约 {credits.toLocaleString()} 积分
            </span>
          )}
        </div>
        <Button
          type="button"
          size="sm"
          variant={active ? "default" : "outline"}
          className="mt-auto"
          data-testid={`real-sample-use-${t.id}`}
          onClick={onUse}
        >
          {active ? "正在用这个" : "用这个拍"}
        </Button>
      </div>
    </div>
  );
}
